import type { MouseEventHandler } from 'react';
import type { OrderItem } from '../../graphql/query/getOrder';
import { useShipmentQuantity } from '../../hooks/useShipmentQuantity';
import { useItemFormIntegration } from '../../hooks/useItemFormIntegration';
import {
  ItemCardView,
  ItemCardButtonView,
  type ItemCardViewProps,
} from './ItemCardView';

type ItemCardProps = Omit<
  ItemCardViewProps,
  'remainingQuantity' | 'isShipmentScopeReason' | 'control'
> & {
  // When true, enables automatic quantity setting based on shipment scope reasons
  enableQuantityAutoSetting?: boolean;
};

/**
 * Logic container for ItemCard that handles workflow and form integration.
 * Calls hooks and passes data to ItemCardView for rendering.
 */
export const ItemCard = ({
  item,
  itemId,
  enableQuantityAutoSetting = false,
  ...rest
}: ItemCardProps) => {
  // Always call hooks (Rules of Hooks), but they handle undefined/null gracefully
  // Cast to OrderItem | undefined - hooks check for required fields internally
  const remainingQuantity = useShipmentQuantity(
    itemId,
    item as OrderItem | undefined,
  );
  const { control, isShipmentScopeReason } = useItemFormIntegration(
    itemId,
    enableQuantityAutoSetting,
    remainingQuantity,
  );

  return (
    <ItemCardView
      item={item}
      itemId={itemId}
      remainingQuantity={remainingQuantity}
      isShipmentScopeReason={isShipmentScopeReason}
      control={control}
      {...rest}
    />
  );
};

/**
 * Logic container for ItemCardButton that handles workflow and form integration.
 */
export const ItemCardButton = (
  props: ItemCardProps & {
    onClick: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
  },
) => {
  const {
    item,
    itemId,
    enableQuantityAutoSetting = false,
    onClick,
    disabled,
    ...rest
  } = props;

  // Always call hooks (Rules of Hooks), but they handle undefined/null gracefully
  // Cast to OrderItem | undefined - hooks check for required fields internally
  const remainingQuantity = useShipmentQuantity(
    itemId,
    item as OrderItem | undefined,
  );
  const { control, isShipmentScopeReason } = useItemFormIntegration(
    itemId,
    enableQuantityAutoSetting,
    remainingQuantity,
  );

  return (
    <ItemCardButtonView
      item={item}
      itemId={itemId}
      remainingQuantity={remainingQuantity}
      isShipmentScopeReason={isShipmentScopeReason}
      control={control}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    />
  );
};

// Re-export types for convenience
export type { Detail } from './ItemCardView';
